'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import dynamic from 'next/dynamic'

import { useTranslations, useLocale } from 'next-intl'
import { usePathname, useRouter } from '@/src/i18n/navigation'

import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { setCurrency } from '@/src/redux/slices/currencySlice'
import { languageOptions, currencyOptions } from '@/src/constants/header'
import { useGetCurrencyDataQuery } from '@/src/api/rtkQuery/currencyApi'
import { getLanguageByCode } from '@/src/utils/language'
import { mapApiCurrencyOptions } from '@/src/utils/currency'
import { setLanguageCookie } from '@/src/utils/language'

import { Header } from './Header'

// Dynamically load SignInModal to reduce initial bundle size
const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ './SignInModal/SignInModalWrapper'),
    { ssr: false }
)

interface HeaderContainerProps {
    hideSignIn?: boolean
}

const HeaderContainer: React.FC<HeaderContainerProps> = ({ hideSignIn = false }) => {
    const [isMounted, setIsMounted] = useState(false)
    const [modals, setModals] = useState<{ signIn: boolean }>({ signIn: false })

    // Get current locale from URL
    const locale = useLocale()
    const localePrefix = React.useMemo(() => `/${locale}`, [locale])

    // Use refs for menu anchors to avoid unnecessary re-renders
    const userMenuAnchorRef = useRef<HTMLElement | null>(null)
    const languageMenuAnchorRef = useRef<HTMLElement | null>(null)
    const currencyMenuAnchorRef = useRef<HTMLElement | null>(null)

    // Track menu open states for re-rendering when needed
    const [menuStates, setMenuStates] = useState<{
        user: boolean
        language: boolean
        currency: boolean
    }>({ user: false, language: false, currency: false })

    // Initialize selected language based on current locale (using utility)
    const [selected, setSelected] = useState({
        language: getLanguageByCode(locale),
        currency: currencyOptions[0]
    })

    // Fetch currency list from API
    const { data: currencyData, isLoading: isCurrencyLoading, isError: isCurrencyError } = useGetCurrencyDataQuery()

    // Map API data to expected shape for dropdown (using utility)
    const apiCurrencyOptions = React.useMemo(() => mapApiCurrencyOptions(currencyData), [currencyData])

    const currency = useAppSelector(state => state.currency.selected)
    const dispatch = useAppDispatch()

    const t = useTranslations('header')
    const tCurrency = useTranslations('Currency')
    const user = useAppSelector(selectAuth)
    const pathname = usePathname()
    const router = useRouter()

    // Check if we're on itinerary page - hide sign-in option on itinerary page
    const isItineraryPage = pathname.includes('/flights/itinerary')
    // Check if we're on signin page - hide sign-in option on signin page
    const isSignInPage = pathname.includes('/signin')
    const shouldHideSignInOnItinerary = isItineraryPage
    const shouldHideSignInOnSignInPage = isSignInPage

    // Define which routes should show the language/currency dropdown
    const showLangCurrDropdown = React.useMemo(() => {
        if (pathname === '/' || /^\/[a-z]{2}$/.test(pathname)) return true
        if (pathname === '/flights' || /^\/[a-z]{2}\/flights\/?$/.test(pathname)) return true
        if (pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)) return true
        if (pathname.includes('/offers')) return true // Show on offers page
        return false
    }, [pathname])

    const showDeals = React.useMemo(() => {
        if (pathname === '/' || /^\/[a-z]{2}$/.test(pathname)) return true
        if (pathname === '/flights' || /^\/[a-z]{2}\/flights\/?$/.test(pathname)) return true
        if (pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)) return true
        return false
    }, [pathname])

    // Locale-prefixed nav links (match CT header style)
    const flyinNavLinks = React.useMemo(
        () => [
            { label: 'Flights', href: `${localePrefix}/flights` },
            { label: 'Hotels', href: `${localePrefix}/hotels` }
        ],
        [localePrefix]
    )

    // Check if current page is offers page
    const isOffersPage = pathname.includes('/offers')

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    useEffect(() => {
        if (!isMounted) return

        // Update currency if needed
        if (!isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0) {
            const found = apiCurrencyOptions.find((opt: any) => opt.code === currency)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        }
    }, [isMounted, currency, apiCurrencyOptions, isCurrencyLoading, isCurrencyError])

    // Sync language selection with locale changes (using utility)
    useEffect(() => {
        const langOption = getLanguageByCode(locale)
        setSelected(prev => ({
            ...prev,
            language: langOption
        }))
    }, [locale])

    const options = {
        language: languageOptions,
        currency:
            !isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0
                ? apiCurrencyOptions
                : currencyOptions // fallback to static
    }

    const onMenuOpen = useCallback((menu: 'user' | 'language' | 'currency', event: React.MouseEvent<HTMLElement>) => {
        if (menu === 'user') {
            userMenuAnchorRef.current = event.currentTarget
        } else if (menu === 'language') {
            languageMenuAnchorRef.current = event.currentTarget
        } else if (menu === 'currency') {
            currencyMenuAnchorRef.current = event.currentTarget
        }
        setMenuStates(prev => ({ ...prev, [menu]: true }))
    }, [])

    const onMenuClose = useCallback((menu: 'user' | 'language' | 'currency') => {
        if (menu === 'user') {
            userMenuAnchorRef.current = null
        } else if (menu === 'language') {
            languageMenuAnchorRef.current = null
        } else if (menu === 'currency') {
            currencyMenuAnchorRef.current = null
        }
        setMenuStates(prev => ({ ...prev, [menu]: false }))
    }, [])

    const onSelect = useCallback(
        (type: 'language' | 'currency', value: any) => {
            setSelected(prev => ({ ...prev, [type]: value }))
            if (type === 'currency') {
                dispatch(setCurrency({ code: value.code, symbol: value.symbol }))
            } else if (type === 'language') {
                // Set language cookie
                setLanguageCookie(value.code)

                // Switch locale in URL path
                const currentPath = window.location.pathname
                const currentSearch = window.location.search
                const currentHash = window.location.hash

                // Replace the locale in the path (assuming it's always /en/ or /ar/ at the start)
                let newPath = currentPath
                if (currentPath.startsWith('/en')) {
                    newPath = value.code === 'ar' ? currentPath.replace(/^\/en/, '/ar') : currentPath
                } else if (currentPath.startsWith('/ar')) {
                    newPath = value.code === 'en' ? currentPath.replace(/^\/ar/, '/en') : currentPath
                } else {
                    // If no locale in path, add it
                    newPath = `/${value.code}${currentPath}`
                }

                // Navigate to the new locale URL
                window.location.href = newPath + currentSearch + currentHash
            }
            onMenuClose(type)
        },
        [dispatch, onMenuClose]
    )

    const onModalOpen = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: true }))
    }, [])
    const onModalClose = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: false }))
    }, [])

    const handleSignInModalClose = useCallback(() => {
        onModalClose('signIn')
    }, [onModalClose])
    const handleUserMenuClose = useCallback(() => onMenuClose('user'), [onMenuClose])

    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <Header
                navLinks={flyinNavLinks}
                options={options}
                selected={selected}
                menuAnchors={{
                    user: menuStates.user ? userMenuAnchorRef.current : null,
                    language: menuStates.language ? languageMenuAnchorRef.current : null,
                    currency: menuStates.currency ? currencyMenuAnchorRef.current : null
                }}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                onSelect={onSelect}
                modals={modals}
                onModalOpen={onModalOpen}
                onModalClose={onModalClose}
                t={t}
                tCurrency={tCurrency}
                user={displayUser}
                dispatch={dispatch}
                showLangCurrDropdown={showLangCurrDropdown}
                showDeals={showDeals}
                isOffersPage={isOffersPage}
                hideSignIn={hideSignIn || shouldHideSignInOnItinerary || shouldHideSignInOnSignInPage}
                isMounted={isMounted}
            />
            <SignInModalWrapper
                open={modals.signIn}
                onClose={handleSignInModalClose}
                onMenuClose={handleUserMenuClose}
            />
        </>
    )
}

export { HeaderContainer as Header }
export default HeaderContainer
